<template>
  <el-drawer
    v-model="payDrawerOpen"
    v-if="payDrawerOpen"
    :direction="'rtl'"
    @open="openPayDialog"
    :lock-scroll="false"
    class="overflow-scroll"
  >
    <div class="px-5" style="margin-top: -20px !important">
      <multi-currency-payment-input
        v-model:total-paid="transaction.totalPaidAmount"
        v-model:change-due="transaction.changeDueAmount"
        :totalDue="transaction.totalDueAmount"
        :iqdCashOnly="true"
        :usePaidLabel="true"
        :hideCurrency="true"
      />

      <div class="mt-4">
        <div><span class="text-red-500 text-xl">*</span>{{ t('creditedAccount') }}</div>
        <div>
          <el-select
            id="credited-acount"
            v-model="transaction.debitedAccountID"
            :trigger-on-focus="false"
            value-key="name"
            class="w-full"
            filterable
          >
            <el-option
              :key="registerAccount.id"
              :label="$ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName"
              :value="registerAccount.id"
            >
              <div class="w-full bg-green-100">
                {{ $ctx.locale === 'ar' ? registerAccount.arName : registerAccount.enName }}
              </div>
            </el-option>

            <el-option
              v-for="safe in safes"
              :key="safe.id"
              :label="$ctx.locale === 'ar' ? safe.arName : safe.enName"
              :value="safe.id"
            >
              <div class="w-full bg-green-100">
                {{ $ctx.locale === 'ar' ? safe.arName : safe.enName }}
              </div>
            </el-option>

            <el-option
              v-for="bank in banks"
              :key="bank.id"
              :label="$ctx.locale === 'ar' ? bank.arName : bank.enName"
              :value="bank.id"
            >
              <div class="w-full bg-green-100">
                {{ $ctx.locale === 'ar' ? bank.arName : bank.enName }}
              </div>
            </el-option>

            <el-option
              v-for="option in accounts"
              :key="option.id"
              :label="$ctx.locale === 'ar' ? option.arName : option.enName"
              :value="option.id"
            ></el-option>
          </el-select>
        </div>
      </div>

      <div class="mt-4">
        <div>{{ t('notes') }}</div>
        <div>
          <el-input v-model="transaction.description" :rows="2" type="textarea" placeholder="" />
        </div>
      </div>

      <div class="flex items-center justify-between my-2">
        <horizontal-button
          class="text-lg complete-payment"
          :title="t('buttons.completePay')"
          @click="openCashierPinDialog()"
          :rounded="false"
        />
      </div>
    </div>
  </el-drawer>
  <el-dialog v-model="cashierPinDialogOpen" width="30%">
    <div>
      <!-- Enter the pin -->
      <div class="my-2">
        <p>{{ t('enterCashierPin') }}</p>
      </div>

      <!-- password input up to 6 chars -->
      <div class="my-2">
        <el-input
          id="password"
          v-model="cashierPin"
          type="password"
          required
          show-password
          autofocus
          dir="ltr"
          :maxlength="4"
          :minlength="4"
          @keyup.enter="validateSubmit()"
          ref="cashierPinInput"
        />
      </div>

      <div class="flex items-center justify-between mb-2">
        <horizontal-button
          class="text-lg complete-payment"
          :title="t('buttons.confirm')"
          @click="validateSubmit()"
          :rounded="false"
        />
      </div>
    </div>
  </el-dialog>
  <el-button v-if="totalStoreCreditBalanceAmount > 0" type="primary" @click="openPayDialog()">{{
    t('payOut')
  }}</el-button>
</template>
<script setup lang="ts">
import { useHTTP } from '@/plugins/http'
import { nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import MultiCurrencyPaymentInput from '@/components/money/MultiCurrencyPaymentInput.vue'
import { Account } from '@/models/accounting/Account'
import { useContext } from '@/plugins/context'
import { Transaction } from '@/models/accounting/Transaction'
import { useSettingsStore } from '@/stores/settingsStore'
import { ElMessage } from 'element-plus'
import Customer from '@/models/customer/Customer'
import HorizontalButton from '@/components/HorizontalButton.vue'

const http = useHTTP()
const ctx = useContext()
const { t } = useI18n({
  messages: {
    en: {
      payOut: 'Pay Out',
      enterCashierPin: 'Enter cashier pin',
      errCashierPinRequired: 'Cashier pin is required',
      notes: 'Notes',
      creditedAccount: 'Pay customer credit from account:',
      payFromAccountRequired: 'Please select payment account',
      buttons: {
        completePay: 'Process Payment',
        confirm: 'Confirm'
      },
      payoutCustomerCreditSuccess: 'Customer credit payout successful'
    },
    ar: {
      payOut: 'ادفع المبلغ',
      enterCashierPin: 'أدخل رقم كود الموظف',
      errCashierPinRequired: 'رقم كود الموظف مطلوب',
      notes: 'الملاحظات',
      creditedAccount: 'اختر حساب الدفع',
      payFromAccountRequired: 'يرجى تحديد حساب الدفع',
      buttons: {
        completePay: 'إتمام الدفع',
        confirm: 'تأكيد'
      },
      payoutCustomerCreditSuccess: 'تم سحب رصيد الزبون بنجاح'
    }
  }
})

interface PayoutCustomerCreditTransaction {
  totalPaidAmount: number
  changeDueAmount: number
  totalDueAmount: number
  currency: string
  precision: number
  description: string
  debitedAccountID: string
}

const { totalStoreCreditBalanceAmount, customerID } = defineProps<{
  totalStoreCreditBalanceAmount: number
  customerID: string
}>()

const emit = defineEmits(['payoutSuccess'])

const accounts = ref<Account[]>([])
const registerAccount = ref<Account>(new Account())
const safes = ref<Account[]>([])
const banks = ref<Account[]>([])
const payDrawerOpen = ref(false)
const transaction = ref<PayoutCustomerCreditTransaction>({
  totalPaidAmount: 0,
  changeDueAmount: 0,
  totalDueAmount: 0,
  currency: ctx.currency,
  precision: ctx.currencyPrecision,
  description: '',
  debitedAccountID: ''
})
const cashierPin = ref('')
const cashierPinInput = ref()
const cashierPinDialogOpen = ref(false)

const openPayDialog = () => {
  payDrawerOpen.value = true
}

watch(
  () => totalStoreCreditBalanceAmount,
  (newVal) => {
    console.log('totalStoreCreditBalanceAmount', totalStoreCreditBalanceAmount)
    transaction.value.totalDueAmount = totalStoreCreditBalanceAmount
  }
)

watch(
  () => transaction.value.totalPaidAmount,
  (newVal) => {
    if (newVal && newVal > totalStoreCreditBalanceAmount) {
      transaction.value.totalPaidAmount = totalStoreCreditBalanceAmount
    }
  }
)

onMounted(async () => {
  try {
    transaction.value.totalDueAmount = totalStoreCreditBalanceAmount
    registerAccount.value = Account.from(
      await http.get<Account>(`${Account.ENDPOINT}/${ctx.currentRegister.accountID}`)
    )
    const safesResp = await http.get<Account[]>('accounting/accounts/safes?skip_balance=true')
    safes.value = safesResp.map((s) => Account.from(s))
    const banksResp = await http.get<Account[]>('accounting/accounts/banks?skip_balance=true')
    banks.value = banksResp.map((b) => Account.from(b))
  } catch (error) {
    console.error(error)
  }
})

async function openCashierPinDialog() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    cashierPinDialogOpen.value = true

    nextTick(() => {
      setTimeout(() => {
        cashierPinInput.value.focus()
      }, 0)
    })
    return
  }
  validateSubmit()
}

async function validateSubmit() {
  if (useSettingsStore().getValue('is_cashier_pin_required') == 'true') {
    if (cashierPin.value === '') {
      cashierPinDialogOpen.value = false
      ElMessage.error({
        message: t('errCashierPinRequired')
      })
      return
    }
  }
  if (!transaction.value.debitedAccountID) {
    ElMessage.error({
      message: t('payFromAccountRequired')
    })
    return
  }

  try {
    const resp = await http.post(
      Account.ENDPOINT + '/' + customerID + '/payout-customer-credit',
      transaction.value,
      {
        headers: { pin: cashierPin.value }
      }
    )

    if (resp) {
      cashierPinDialogOpen.value = false
      payDrawerOpen.value = false
      ElMessage.success({
        message: t('payoutCustomerCreditSuccess')
      })
      emit('payoutSuccess')
    }
  } catch (error) {
    ElMessage.error(error?.title)
  }
}
</script>
